import moment from "moment";

import { isWeekend } from "util/utils";

/**
 * A single holiday record, matching the shape of `state.Holidays.allHolidays.data`
 * entries used across the timeoff screens.
 */
export interface HolidayRecord {
  StartDate: string;
  EndDate?: string | null;
  Name?: string | null;
}

export type HolidaysList = HolidayRecord[] | null | undefined;

export interface DateRangeValidationResult {
  isValid: boolean;
  error: string;
}

/**
 * The window that the time-off (leave) dates must fall inside, anchored to the
 * swap day (the holiday actually worked). See `getSwapDateWindow` below.
 */
export interface SwapDateWindow {
  lower: Date;
  upper: Date;
}

export interface ValidateLeaveDatesAgainstSwapWindowParams {
  /** `swapDateFrom` — the holiday/weekend day worked. This is the anchor. */
  holidayDate: Date | string;
  /** `dateFrom` — the leave/time-off start date being validated against the window. */
  dateFrom: Date | string;
  /** `dateTo` — the leave/time-off end date being validated against the window. */
  dateTo: Date | string;
  /**
   * Raw `SWAP_DATE_FROM_ALLOWED_IN_PAST` `Platform_Config` value, e.g. `'0M'` or `'1M'`,
   * as returned on the leave-type. See `getSwapDateWindow` for parsing rules.
   */
  swapDateFromAllowedInPast?: string;
}

/** Preserves the exact copy that existed in both SwappedLeavesDateSelection and Daterange. */
export const SWAP_DATE_RANGE_ERROR = "Swap days should only include holidays or weekends.";

/** Raised when the leave range itself is inverted (end before start), independent of the window. */
export const LEAVE_DATE_RANGE_ORDER_ERROR = "Time-off end date cannot be before the start date.";

/**
 * Fallback used by callers when the leave-type object doesn't (yet) carry a
 * `swapDateFromAllowedInPast` field from the backend. Kept in sync with this module's own
 * `DEFAULT_ALLOWED_IN_PAST` — `getSwapDateWindow` already defaults to `'0M'` when its
 * `swapDateFromAllowedInPast` argument is missing, so this constant exists purely so
 * call sites can express the fallback explicitly (e.g. `leaveType?.swapDateFromAllowedInPast
 * ?? DEFAULT_SWAP_DATE_FROM_ALLOWED_IN_PAST`) rather than relying on an implicit default.
 */
export const DEFAULT_SWAP_DATE_FROM_ALLOWED_IN_PAST = "0M";

/**
 * Returns 31 December of `referenceDate`'s year (default: now), normalised to start-of-day.
 *
 * Used to cap the swapped-leave calendars (PRD-1879 QA follow-up) so that no date past the
 * end of the CURRENT calendar year is ever selectable, even when a leave-type's cycle end
 * (`validDates.endDate` / `nextCycle.maxFilingDate`) lands in the next calendar year. The
 * year is always derived from `referenceDate` at call time — never hardcoded — so callers
 * should not memoize the result across a year boundary without re-deriving it.
 */
export function getStartOfCurrentCalendarYear(referenceDate: Date = new Date()): Date {
  return moment(referenceDate).startOf("year").startOf("day").toDate();
}

export function getEndOfCurrentCalendarYear(referenceDate: Date = new Date()): Date {
  return moment(referenceDate).endOf("year").startOf("day").toDate();
}

/**
 * Computes the swap-days calendar's default preselected date (PRD-1879 QA follow-up).
 *
 * `findNearestSaturday` (util/utils) always returns the NEXT Saturday, which in the last few
 * days of December rolls into the next calendar year — past the swap-days calendar's own
 * end-of-year cap. When `nearestSaturday` falls outside `[minDate, maxDate]`, this steps back
 * 7 days to the PREVIOUS Saturday instead (still a valid weekend swap day). If even that
 * previous Saturday falls outside the bounds, returns `null` so the caller renders no
 * preselection at all, rather than silently preselecting a date the user can't actually pick
 * (either because it's disabled by the calendar-year cap, or because it isn't a weekend/holiday).
 */
export function getSwapDaysDefaultPreselection(
  nearestSaturday: Date,
  minDate?: Date | null,
  maxDate?: Date | null
): Date | null {
  const isWithinBounds = (date: Date): boolean => {
    const time = date.getTime();
    if (minDate && time < minDate.getTime()) return false;
    if (maxDate && time > maxDate.getTime()) return false;
    return true;
  };

  if (isWithinBounds(nearestSaturday)) {
    return nearestSaturday;
  }

  const previousSaturday = moment(nearestSaturday).subtract(7, "days").toDate();
  return isWithinBounds(previousSaturday) ? previousSaturday : null;
}

/**
 * Finds the name of the holiday (if any) that falls on `date`, matching against a list of
 * holiday records shaped like `state.Holidays.allHolidays.data`.
 */
export function getHolidayName(date: Date, holidays: HolidaysList): string | null {
  const holiday = holidays?.find(
    (leave) =>
      moment(date).isSame(moment(leave?.StartDate?.split("T")[0]), "day") ||
      (!!leave?.EndDate &&
        moment(date).isBetween(
          moment(leave?.StartDate?.split("T")[0]),
          moment(leave?.EndDate?.split("T")[0]),
          null,
          "[]"
        ))
  );
  return holiday?.Name || null;
}

/** A swap day is valid only if it is a weekend or a public holiday. */
export function isValidSwapDate(date: Date, holidays: HolidaysList): boolean {
  return isWeekend(date) || !!getHolidayName(date, holidays);
}

/**
 * Validates that every day in [startDate, endDate] is a weekend or a holiday.
 * A single-day range is always considered valid (matches the pre-existing behaviour).
 */
export function validateSwapDateRange(
  startDate: Date | string,
  endDate: Date | string,
  holidays: HolidaysList
): DateRangeValidationResult {
  const dates = [moment(startDate), moment(endDate)].sort((a, b) => a.diff(b));
  const start = dates[0];
  const end = dates[1];

  if (start.isSame(end, "day")) {
    return { isValid: true, error: "" };
  }

  const current = start.clone();

  while (current.isSameOrBefore(end)) {
    if (!isValidSwapDate(current.toDate(), holidays)) {
      return {
        isValid: false,
        error: SWAP_DATE_RANGE_ERROR,
      };
    }
    current.add(1, "day");
  }

  return { isValid: true, error: "" };
}

/**
 * Duration parsed out of a `SWAP_DATE_FROM_ALLOWED_IN_PAST`-style config string.
 * `unit` is a moment shorthand duration unit: `"M"` for months, `"d"` for days.
 */
interface AllowedInPastDuration {
  amount: number;
  unit: "M" | "d";
}

/** Matches the migration's repointed default: no config ⇒ no widening of the window. */
const DEFAULT_ALLOWED_IN_PAST: AllowedInPastDuration = { amount: 0, unit: "M" };

/**
 * Parses the raw `swapDateFromAllowedInPast` config string with the same shape the backend
 * uses (`SwapLeaveService.getSwapDateWindow`, `app/timeoff-v2/swapLeave.service.ts`):
 * `\d+\s*[Md]`, case-INSENSITIVE — either case of `M` means months, either case of `D`
 * means days.
 *
 * The backend matches with `/^(\d+)\s*([Md])$/i` and then explicitly normalizes the matched
 * letter (`unitChar.toLowerCase() === 'm' ? 'months' : 'days'`) before ever passing it to
 * `moment().subtract()`, so a raw matched character is never used un-normalized. This parser
 * mirrors that: the matched unit letter is lowercased and compared against `'m'` to decide
 * months vs. days, so `'3M'`/`'3m'` both mean 3 months and `'3D'`/`'3d'` both mean 3 days.
 *
 * Missing, empty, or otherwise malformed input (e.g. `'abc'`) falls back to `'0M'`.
 */
function parseSwapDateFromAllowedInPast(config?: string | null): AllowedInPastDuration {
  const match = config?.trim().match(/^(\d+)\s*([Md])$/i);
  if (!match) {
    return DEFAULT_ALLOWED_IN_PAST;
  }
  const unit: AllowedInPastDuration["unit"] = match[2].toLowerCase() === "m" ? "M" : "d";
  return { amount: Number(match[1]), unit };
}

/**
 * Computes the leave (time-off) window anchored to the swap day (the holiday actually
 * worked), per PRD-1879:
 *
 *   lower = startOfMonth(holidayDate) - swapDateFromAllowedInPast   ('0M' ⇒ 1st of the holiday's month)
 *   upper = 31 December of year(holidayDate)
 *
 * Mirrors `SwapLeaveService.getSwapDateWindow` in the backend (`app/timeoff-v2/swapLeave.service.ts`),
 * except the anchor here is correctly the holiday (`swapDateFrom`), not the leave (`dateFrom`).
 */
export function getSwapDateWindow(holidayDate: Date | string, swapDateFromAllowedInPast?: string | null): SwapDateWindow {
  const { amount, unit } = parseSwapDateFromAllowedInPast(swapDateFromAllowedInPast);

  const anchor = moment(holidayDate);

  const lower = anchor.clone().startOf("month").startOf("day").subtract(amount, unit).toDate();
  const upper = anchor.clone().endOf("year").startOf("day").toDate();

  return { lower, upper };
}

/**
 * Validates the leave (time-off) range `[dateFrom, dateTo]` against the swap window anchored
 * to `holidayDate` (the swap day / holiday, `swapDateFrom`), per PRD-1879:
 *
 *   require: lower ≤ dateFrom ≤ dateTo ≤ upper
 *
 * Also rejects `dateTo < dateFrom` outright (with a distinct message), independent of the
 * window, since an inverted leave range is never valid regardless of the swap window.
 */
export function validateLeaveDatesAgainstSwapWindow({
  holidayDate,
  dateFrom,
  dateTo,
  swapDateFromAllowedInPast,
}: ValidateLeaveDatesAgainstSwapWindowParams): DateRangeValidationResult {
  const from = moment(dateFrom).startOf("day");
  const to = moment(dateTo).startOf("day");

  if (to.isBefore(from)) {
    return { isValid: false, error: LEAVE_DATE_RANGE_ORDER_ERROR };
  }

  const { lower, upper } = getSwapDateWindow(holidayDate, swapDateFromAllowedInPast);
  const min = moment(lower).startOf("day");
  const max = moment(upper).startOf("day");

  if (from.isBefore(min) || to.isAfter(max)) {
    return {
      isValid: false,
      error: `Time-off days must fall between ${moment(lower).format("D MMM YYYY")} and ${moment(upper).format(
        "D MMM YYYY"
      )}.`,
    };
  }

  return { isValid: true, error: "" };
}
